import { Box } from 'src/design/atoms/Box';
import { Group } from 'src/design/atoms/Group';
import { Tooltip } from 'src/design/atoms/Tooltip';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import React from 'react';
import { MilestoneMoveDirection } from 'src/api/FiretrolApiClient';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';

export interface IMilestoneMoveActionIconsProps {
  isFirst: boolean;
  isLast: boolean;
  blockedReason?: string;
  pending: boolean;
  onMove: (direction: MilestoneMoveDirection) => void;
}

export interface IMoveIconProps {
  label: string;
  tooltip: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

const MoveIcon: React.FC<IMoveIconProps> = ({
  label,
  tooltip,
  disabled,
  onClick,
  children,
}) => (
  // The Box keeps the tooltip reachable: a disabled button gets no hover events.
  <Tooltip label={tooltip}>
    <Box>
      <BasicActionIcon
        aria-label={label}
        color="blue"
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
      >
        {children}
      </BasicActionIcon>
    </Box>
  </Tooltip>
);

/** @registry Up/down arrow action icons that move a milestone item inside its phase; disabled at the group's ends, while a move is pending, or when a blocked reason is given. */
export const MilestoneMoveActionIcons: React.FC<
  IMilestoneMoveActionIconsProps
> = ({ isFirst, isLast, blockedReason, pending, onMove }) => (
  <Group gap={4} wrap="nowrap">
    <MoveIcon
      label="Move up"
      tooltip={
        blockedReason ?? (isFirst ? 'Already first in its phase' : 'Move up')
      }
      disabled={!!blockedReason || isFirst || pending}
      onClick={() => onMove(MilestoneMoveDirection.Up)}
    >
      <IconChevronUp size={18} />
    </MoveIcon>
    <MoveIcon
      label="Move down"
      tooltip={
        blockedReason ?? (isLast ? 'Already last in its phase' : 'Move down')
      }
      disabled={!!blockedReason || isLast || pending}
      onClick={() => onMove(MilestoneMoveDirection.Down)}
    >
      <IconChevronDown size={18} />
    </MoveIcon>
  </Group>
);
